// Excel + PDF exports of a collections page, built from exactly what the screen
// is showing — the rows left after every filter and the search box, in the order
// they appear. Never re-fetches: what you see is what you get.
//
// Shared by Payment Collections and Current Payment Collections, which differ
// only in whether they carry an SL number.
//
// Both formats open with a block naming the filters that were in force, so a
// sheet or print-out that gets passed around still explains itself — "whose
// collections are these, and for which month?".

import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';

import { billTypeLabel } from '../constants/billingTypes';
import { effectiveStatus, outstandingOf, collectedOf, clientLabel } from './billStatus';

export interface CollectionExportOptions {
  /** The filtered, sorted bills currently on screen. */
  rows: any[];
  /** Reference numbers by patient id. Omitted on the page that has no SL column. */
  slNos?: Record<number, number | null>;
  /** "Payment Collections" or "Current Payment Collections". */
  title: string;
  /** e.g. "August 2026". */
  periodLabel: string;
  /** [label, value] pairs describing the filters in force. */
  summary: [string, string][];
  /** Base file name, before the extension and punctuation stripping. */
  fileBase: string;
}

interface Col {
  label: string;
  /** Excel column width, in characters. */
  width: number;
  /** Relative weight used to share out the PDF's usable width. */
  weight: number;
  numeric?: boolean;
  get: (b: any) => any;
}

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const fmtAmount = (n: number) =>
  n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** The bill's printed number — its invoice series when it has one. */
const billNo = (b: any) =>
  b.bill_type === 'consumable_return'
    ? (b.invoice_no || `R-${b.bill_no}`)
    : (b.invoice_no || `#${b.bill_no}`);

const buildColumns = (slNos?: Record<number, number | null>): Col[] => [
  // Only where the page shows it — the main collections page has no SL column,
  // and an empty one in the export would just prompt questions.
  ...(slNos
    ? [{ label: 'SL', width: 6, weight: 5, get: (b: any) => slNos[b.patient_id] ?? '' }]
    : []),
  { label: 'Client',        width: 26, weight: 20, get: (b: any) => clientLabel(b) },
  { label: 'Patient',       width: 24, weight: 18, get: (b: any) => b.patient_name || '' },
  { label: 'Duty Type',     width: 12, weight: 10, get: (b: any) => b.duty_type || '' },
  { label: 'Bill No',       width: 20, weight: 15, get: billNo },
  { label: 'Type',          width: 14, weight: 11, get: (b: any) => billTypeLabel(b.bill_type) },
  { label: 'Invoice Period', width: 26, weight: 20,
    get: (b: any) => `${fmtDate(b.invoice_period_start)} – ${fmtDate(b.invoice_period_end)}` },
  // Billed = Collected + Pending, the same three figures the screen shows and
  // named the same way, so an exported total can be checked against the page.
  { label: 'Billed',        width: 15, weight: 13, numeric: true, get: (b: any) => Number(b.total_amount) || 0 },
  { label: 'Collected',     width: 15, weight: 13, numeric: true, get: (b: any) => collectedOf(b) },
  { label: 'Pending',       width: 15, weight: 13, numeric: true, get: (b: any) => outstandingOf(b) },
  // Derived, so a part-paid bill reads Pending here exactly as on screen.
  { label: 'Status',        width: 12, weight: 10, get: (b: any) => effectiveStatus(b) },
  { label: 'Case Manager',  width: 20, weight: 15, get: (b: any) => b.case_manager || '' },
  { label: 'Remarks',       width: 30, weight: 22, get: (b: any) => b.collection_remarks || '' },
  { label: 'Exp. Payment',  width: 16, weight: 13, get: (b: any) => fmtDate(b.expected_payment_date) },
];

const safeName = (s: string) => s.replace(/[^\w]+/g, '-').replace(/^-|-$/g, '');

const totalsOf = (rows: any[]) => ({
  billed: rows.reduce((s, b) => s + (Number(b.total_amount) || 0), 0),
  collected: rows.reduce((s, b) => s + collectedOf(b), 0),
  pending: rows.reduce((s, b) => s + outstandingOf(b), 0),
});

// ── Excel ───────────────────────────────────────────────────────────────────

// Row 1 and column A are left empty as a margin, matching the ledger export.
const FIRST_ROW = 2;
const FIRST_COL = 2;

export async function downloadCollectionsExcel(o: CollectionExportOptions): Promise<void> {
  const cols = buildColumns(o.slNos);
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Collections');

  ws.getColumn(1).width = 3;
  cols.forEach((c, i) => { ws.getColumn(FIRST_COL + i).width = c.width; });
  const lastCol = FIRST_COL + cols.length - 1;

  let r = FIRST_ROW;
  const banner = (value: string, font: Partial<ExcelJS.Font>) => {
    ws.getCell(r, FIRST_COL).value = value;
    ws.mergeCells(r, FIRST_COL, r, lastCol);
    ws.getCell(r, FIRST_COL).font = font;
    ws.getCell(r, FIRST_COL).alignment = { horizontal: 'left' };
    r++;
  };

  banner(o.title, { bold: true, size: 14, color: { argb: 'FF0F172A' } });
  banner(o.periodLabel, { size: 10, color: { argb: 'FF64748B' } });
  r++;

  const pairs: [string, string][] = [...o.summary, ['Bills', String(o.rows.length)]];
  pairs.forEach(([label, value]) => {
    ws.getCell(r, FIRST_COL).value = label;
    ws.getCell(r, FIRST_COL).font = { size: 10, bold: true, color: { argb: 'FF64748B' } };
    ws.getCell(r, FIRST_COL + 1).value = value;
    ws.mergeCells(r, FIRST_COL + 1, r, lastCol);
    ws.getCell(r, FIRST_COL + 1).font = { size: 10, color: { argb: 'FF334155' } };
    ws.getCell(r, FIRST_COL + 1).alignment = { horizontal: 'left' };
    r++;
  });

  r++;
  const headerRowIdx = r;
  const headerRow = ws.getRow(headerRowIdx);
  cols.forEach((c, i) => {
    const cell = headerRow.getCell(FIRST_COL + i);
    cell.value = c.label;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2563EB' } };
  });
  r++;

  o.rows.forEach((b, i) => {
    const row = ws.getRow(r);
    cols.forEach((c, ci) => {
      const cell = row.getCell(FIRST_COL + ci);
      cell.value = c.get(b);
      cell.border = { bottom: { style: 'hair', color: { argb: 'FFE2E8F0' } } };
      if (c.numeric) {
        cell.numFmt = '#,##0.00';
        cell.alignment = { horizontal: 'right' };
      }
      if (i % 2 === 1) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
      }
    });
    r++;
  });

  const totals = totalsOf(o.rows);
  const totalRow = ws.getRow(r);
  totalRow.getCell(FIRST_COL).value = 'Total';
  cols.forEach((c, ci) => {
    const cell = totalRow.getCell(FIRST_COL + ci);
    if (c.label === 'Billed') cell.value = totals.billed;
    if (c.label === 'Collected') cell.value = totals.collected;
    if (c.label === 'Pending') cell.value = totals.pending;
    if (c.numeric) { cell.numFmt = '#,##0.00'; cell.alignment = { horizontal: 'right' }; }
    cell.font = { bold: true };
    cell.border = { top: { style: 'thin', color: { argb: 'FF94A3B8' } } };
  });

  ws.views = [{ state: 'frozen', ySplit: headerRowIdx }];
  ws.autoFilter = {
    from: { row: headerRowIdx, column: FIRST_COL },
    to: { row: headerRowIdx, column: lastCol },
  };

  const buf = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buf]), `${safeName(o.fileBase)}.xlsx`);
}

// ── PDF ─────────────────────────────────────────────────────────────────────

const PAGE_W = 297;   // A4 landscape, millimetres
const PAGE_H = 210;
const MARGIN = 10;
const USABLE = PAGE_W - MARGIN * 2;
const ROW_H = 5;

// jsPDF's built-in fonts are WinAnsi-encoded and have no ₹ (U+20B9) glyph, so
// the PDF says "Rs." The Excel uses proper number formatting either way.
const RUPEE = 'Rs.';

export function downloadCollectionsPdf(o: CollectionExportOptions): void {
  const cols = buildColumns(o.slNos);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  const totalWeight = cols.reduce((s, c) => s + c.weight, 0);
  const widths = cols.map(c => (c.weight / totalWeight) * USABLE);

  const fit = (text: string, width: number) => {
    if (!text) return '';
    if (doc.getTextWidth(text) <= width) return text;
    let out = text;
    while (out.length > 1 && doc.getTextWidth(out + '…') > width) out = out.slice(0, -1);
    return out + '…';
  };

  let y = 0;

  const drawTableHeader = () => {
    doc.setFillColor(37, 99, 235);
    doc.rect(MARGIN, y, USABLE, 7, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    let x = MARGIN;
    cols.forEach((c, i) => {
      const label = c.numeric && c.label !== 'SL' ? `${c.label} (${RUPEE})` : c.label;
      if (c.numeric) doc.text(fit(label, widths[i] - 3), x + widths[i] - 1.5, y + 4.7, { align: 'right' });
      else doc.text(fit(label, widths[i] - 3), x + 1.5, y + 4.7);
      x += widths[i];
    });
    y += 7;
  };

  const newPage = (first = false) => {
    if (!first) doc.addPage();
    y = MARGIN;

    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(fit(o.title, USABLE), MARGIN, y + 4);
    y += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(o.periodLabel, MARGIN, y + 3.5);
    y += 6;

    // The filters that produced these rows, two per line to keep it short.
    doc.setFontSize(7.5);
    const pairs: [string, string][] = [...o.summary, ['Bills', String(o.rows.length)]];
    const colW = USABLE / 2;
    pairs.forEach(([label, value], i) => {
      const x = MARGIN + (i % 2) * colW;
      if (i % 2 === 0 && i > 0) y += 4;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 116, 139);
      doc.text(`${label}:`, x, y + 3.2);
      const labelW = doc.getTextWidth(`${label}:`) + 2;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(15, 23, 42);
      doc.text(fit(value, colW - labelW - 4), x + labelW, y + 3.2);
    });
    y += 7;

    doc.setDrawColor(226, 232, 240);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    y += 3;

    drawTableHeader();
  };

  newPage(true);

  doc.setFont('helvetica', 'normal');
  o.rows.forEach((b, i) => {
    if (y + ROW_H > PAGE_H - MARGIN - 6) {
      newPage();
      doc.setFont('helvetica', 'normal');
    }
    if (i % 2 === 1) {
      doc.setFillColor(248, 250, 252);
      doc.rect(MARGIN, y, USABLE, ROW_H, 'F');
    }
    doc.setFontSize(7);
    doc.setTextColor(30, 41, 59);
    let x = MARGIN;
    cols.forEach((c, ci) => {
      const raw = c.get(b);
      if (c.numeric) {
        doc.text(fmtAmount(Number(raw) || 0), x + widths[ci] - 1.5, y + 3.4, { align: 'right' });
      } else {
        doc.text(fit(String(raw ?? ''), widths[ci] - 3), x + 1.5, y + 3.4);
      }
      x += widths[ci];
    });
    doc.setDrawColor(241, 245, 249);
    doc.line(MARGIN, y + ROW_H, PAGE_W - MARGIN, y + ROW_H);
    y += ROW_H;
  });

  if (y + ROW_H > PAGE_H - MARGIN - 6) newPage();
  const totals = totalsOf(o.rows);
  doc.setDrawColor(148, 163, 184);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(15, 23, 42);
  doc.text('Total', MARGIN + 1.5, y + 4);
  let x = MARGIN;
  cols.forEach((c, ci) => {
    if (c.numeric) {
      const v = c.label === 'Billed' ? totals.billed
        : c.label === 'Collected' ? totals.collected
        : c.label === 'Pending' ? totals.pending : null;
      if (v !== null) doc.text(fmtAmount(v), x + widths[ci] - 1.5, y + 4, { align: 'right' });
    }
    x += widths[ci];
  });

  const pages = doc.getNumberOfPages();
  const stamp = new Date().toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(`Generated ${stamp}`, MARGIN, PAGE_H - 5);
    doc.text(`Page ${p} of ${pages}`, PAGE_W - MARGIN, PAGE_H - 5, { align: 'right' });
  }

  doc.save(`${safeName(o.fileBase)}.pdf`);
}
